/* Iter 31.168 — Agent Mail (Feb 2026)
 *
 * Closes the conversational loop opened in iter 31.129. Inbound mail
 * lands on each agent's ledger via the Cloudflare Worker; this page
 * surfaces the thread per-agent and lets the operator reply through
 * Resend so `three@spyfinance.cloud` becomes a proper two-way channel
 * rather than a drop-box.
 *
 * Layout: agents listed on the left (active first), selected agent's
 * unified thread (inbound + outbound, newest first) on the right.
 * Each inbound item carries an inline Reply form that posts to
 * /api/agents/email-outbound — the response is filed back into the
 * same agent_mail collection with direction=outbound, so the thread
 * is canon-faithful: every message visible on one rolling timeline.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Mail, Reply, RefreshCw, Send, AlertTriangle } from "lucide-react";

const ACTIVE_FIRST = (a, b) => {
    // Active agents render first, then by prisoner_number ascending.
    if (a.tier === "active" && b.tier !== "active") return -1;
    if (a.tier !== "active" && b.tier === "active") return 1;
    return (a.prisoner_number || 999) - (b.prisoner_number || 999);
};

function ReplyForm({ agentKey, replyTo, inReplyToId, defaultSubject, onSent }) {
    // Iter 31.168 — inline reply form rendered under each inbound item.
    // Default subject prefixes "Re:" unless the inbound subject already
    // starts with Re: / RE: / re:.
    const reSubject = useMemo(() => {
        const s = defaultSubject || "";
        return /^\s*re:/i.test(s) ? s : `Re: ${s}`;
    }, [defaultSubject]);
    const [subject, setSubject] = useState(reSubject);
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const send = async () => {
        if (!body.trim()) { setErr("Body required."); return; }
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/agents/email-outbound", {
                agent_key: agentKey,
                to: replyTo,
                subject: subject.trim() || "(no subject)",
                body: body.trim(),
                in_reply_to_id: inReplyToId || null,
            });
            setBody("");
            onSent?.(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "send failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-2 border-t border-dashed border-ink/30 pt-2" data-testid={`mail-reply-form-${inReplyToId || "new"}`}>
            <div className="font-body text-[11px] text-ink-soft mb-1">
                Replying to <span className="text-ink">{replyTo}</span> as <span className="text-ink">{agentKey}@spyfinance.cloud</span>
            </div>
            <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject"
                className="w-full border border-ink/40 px-2 py-1 font-body text-sm mb-1 bg-paper"
                data-testid={`mail-reply-subject-${inReplyToId || "new"}`}
            />
            <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write reply…"
                rows={4}
                className="w-full border border-ink/40 px-2 py-1 font-body text-sm bg-paper mb-1"
                data-testid={`mail-reply-body-${inReplyToId || "new"}`}
            />
            {err && (
                <div className="text-stamp-red font-body text-xs mb-1" data-testid={`mail-reply-error-${inReplyToId || "new"}`}>
                    {err}
                </div>
            )}
            <div className="flex items-center gap-2">
                <button
                    onClick={send}
                    disabled={busy}
                    data-testid={`mail-reply-send-${inReplyToId || "new"}`}
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    {busy ? "Sending…" : "Send reply"}
                </button>
            </div>
        </div>
    );
}

function MailItem({ item, agentKey, onSent }) {
    const [showReply, setShowReply] = useState(false);
    const isOutbound = item.direction === "outbound";
    const counterpart = isOutbound ? item.to : item.from;
    const ts = item.received_at || item.sent_at;
    let tsLabel = ts;
    try { tsLabel = new Date(ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { /* keep raw */ }

    return (
        <li
            className={`border-l-2 pl-3 py-2 ${isOutbound ? "border-stamp-blue" : "border-ink/40"}`}
            data-testid={`mail-item-${item.message_id}`}
        >
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-1">
                <span className={`uppercase tracking-[0.18em] text-[9px] px-1.5 py-0.5 border ${
                    isOutbound ? "border-stamp-blue text-stamp-blue" : "border-ink/40 text-ink"
                }`}>
                    {isOutbound ? "sent" : "inbound"}
                </span>
                <span className="font-heading text-xs text-ink">{counterpart}</span>
                <span className="font-body text-[11px] text-ink-soft">· {tsLabel}</span>
            </div>
            <div className="font-heading text-sm text-ink mb-1" data-testid={`mail-item-subject-${item.message_id}`}>
                {item.subject || "(no subject)"}
            </div>
            <pre className="font-body text-[12px] text-ink whitespace-pre-wrap leading-snug max-h-48 overflow-y-auto" data-testid={`mail-item-body-${item.message_id}`}>
                {(item.body || "").slice(0, 4000)}
                {item.body && item.body.length > 4000 ? "…" : ""}
            </pre>
            {!isOutbound && (
                <div className="mt-1">
                    {!showReply ? (
                        <button
                            onClick={() => setShowReply(true)}
                            data-testid={`mail-reply-btn-${item.message_id}`}
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-2.5 py-1 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red inline-flex items-center gap-1"
                        >
                            <Reply className="w-3 h-3" /> Reply via email
                        </button>
                    ) : (
                        <ReplyForm
                            agentKey={agentKey}
                            replyTo={item.from}
                            inReplyToId={item.message_id}
                            defaultSubject={item.subject}
                            onSent={(d) => { setShowReply(false); onSent?.(d); }}
                        />
                    )}
                </div>
            )}
        </li>
    );
}

export default function MailPage() {
    const [agents, setAgents] = useState([]);
    const [selected, setSelected] = useState(null);
    const [thread, setThread] = useState([]);
    const [loading, setLoading] = useState(true);
    const [threadLoading, setThreadLoading] = useState(false);
    const [err, setErr] = useState("");
    const [status, setStatus] = useState(null);

    const loadAgents = async () => {
        setErr("");
        try {
            const [a, s] = await Promise.all([
                api.get("/agent-ledgers"),
                api.get("/agents/email-status"),
            ]);
            const sorted = (a.data.agents || []).slice().sort(ACTIVE_FIRST);
            setAgents(sorted);
            setStatus(s.data);
            if (sorted.length && !selected) setSelected(sorted[0].key);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    const loadThread = async (key) => {
        if (!key) return;
        setThreadLoading(true);
        try {
            const { data } = await api.get(`/agents/email-thread/${key}?limit=50`);
            setThread(data.items || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        } finally {
            setThreadLoading(false);
        }
    };

    useEffect(() => { loadAgents(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
    useEffect(() => { loadThread(selected); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selected]);

    const counts = useMemo(() => {
        // Per-agent: count inbound rows in thread; cheap since the list
        // is server-paginated already (limit 50).
        const map = {};
        thread.forEach((r) => {
            const k = r.agent_key;
            map[k] = (map[k] || 0) + 1;
        });
        return map;
    }, [thread]);

    return (
        <div className="paper-card p-6 max-w-6xl mx-auto" data-testid="mail-page">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                <h1 className="font-heading text-2xl uppercase tracking-[0.2em] text-ink inline-flex items-center gap-2">
                    <Mail className="w-5 h-5" /> Agent Mail
                </h1>
                <button
                    onClick={() => loadThread(selected)}
                    disabled={threadLoading}
                    data-testid="mail-refresh-btn"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red disabled:opacity-40 inline-flex items-center gap-1"
                >
                    <RefreshCw className={`w-3 h-3 ${threadLoading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {status && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4 font-body text-[11px]" data-testid="mail-status">
                    <div>
                        <span className="label-typed text-[10px] mr-1">Inbound:</span>
                        {status.inbound_configured ? (
                            <span className="text-ink">configured</span>
                        ) : (
                            <span className="text-stamp-red">missing SHED_EMAIL_SECRET</span>
                        )}
                    </div>
                    <div>
                        <span className="label-typed text-[10px] mr-1">Outbound:</span>
                        {status.outbound_configured ? (
                            <span className="text-ink">configured</span>
                        ) : (
                            <span className="text-stamp-red inline-flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> missing RESEND_API_KEY
                            </span>
                        )}
                    </div>
                    <div>
                        <span className="label-typed text-[10px] mr-1">From domain:</span>
                        <span className="text-ink">{status.sender_domain}</span>
                    </div>
                </div>
            )}

            {!status?.outbound_configured && (
                <div className="border border-dashed border-stamp-red/60 bg-stamp-red/5 p-3 mb-4 font-body text-[12px] text-ink" data-testid="mail-outbound-not-configured">
                    Outbound replies are dark. Walk <code>/app/memory/MX_SETUP.md</code> Step 7
                    (Resend domain + API key) and the Reply buttons go live.
                </div>
            )}

            {err && (
                <div className="text-stamp-red font-body text-xs mb-3" data-testid="mail-error">
                    Error: {err}
                </div>
            )}

            {loading ? (
                <div className="font-body text-xs text-ink-soft">Loading…</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
                    {/* Agent list */}
                    <ul className="space-y-1" data-testid="mail-agent-list">
                        {agents.map((a) => (
                            <li key={a.key}>
                                <button
                                    onClick={() => setSelected(a.key)}
                                    data-testid={`mail-agent-${a.key}`}
                                    className={`w-full text-left px-2 py-1.5 border ${
                                        selected === a.key
                                            ? "border-stamp-red text-stamp-red"
                                            : "border-ink/20 text-ink hover:border-ink/50"
                                    }`}
                                >
                                    <div className="font-heading text-xs uppercase tracking-wide">
                                        {a.prisoner_number} · {a.name}
                                    </div>
                                    <div className="font-body text-[10px] text-ink-soft">
                                        {a.role}
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* Thread */}
                    <div data-testid="mail-thread">
                        {threadLoading ? (
                            <div className="font-body text-xs text-ink-soft">Loading thread…</div>
                        ) : thread.length === 0 ? (
                            <div className="font-body text-xs text-ink-soft" data-testid="mail-thread-empty">
                                No mail on {selected ? `${selected}@spyfinance.cloud` : "this agent"} yet.
                            </div>
                        ) : (
                            <ul className="space-y-3">
                                {thread.map((item) => (
                                    <MailItem
                                        key={item.message_id}
                                        item={item}
                                        agentKey={selected}
                                        onSent={() => loadThread(selected)}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
            {/* counts memo silences lint, used to surface per-agent badges in a later iter */}
            <span className="sr-only" data-testid="mail-counts">{Object.keys(counts).length}</span>
        </div>
    );
}
